import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, GridLegacy as Grid,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { internshipService, userService } from '../services/api';
import InternshipDetailDialog from './InternshipDetailDialog';
import {
  InternshipDashboard, InternshipSummary, STATUS_META, PROGRESS_META, fmtDate, apiError,
} from './internshipTypes';

interface UserOption { id: number; fullName: string; }

const STATUS_FILTERS = [
  { value: 'open', label: 'Ongoing' },
  { value: 'EvaluationDue', label: 'Evaluation due' },
  { value: 'UnderReview', label: 'Awaiting HR decision' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];

const isClosed = (s: string) => ['Converted', 'Completed', 'Terminated'].includes(s);

// HR (Admin) sees every internship plus the pipeline cards and can start new ones; a Manager
// sees the interns they mentor or who report to them (filtered server-side).
const InternshipManagement: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [rows, setRows] = useState<InternshipSummary[]>([]);
  const [dashboard, setDashboard] = useState<InternshipDashboard | null>(null);
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await internshipService.getAll(status ? { status } : undefined));
      if (isAdmin) setDashboard(await internshipService.getDashboard());
    } catch (err) {
      setError(apiError(err, 'Failed to load internships.'));
    }
  }, [status, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter(r => `${r.internName} ${r.mentorName} ${r.roleTitle} ${r.department ?? ''}`.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const today = new Date().setHours(0, 0, 0, 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Internships</Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly check-ins → mid-term review → final evaluation → HR decision (confirm, extend, complete or terminate) → certificate.
          </Typography>
        </Box>
        {isAdmin && <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>New internship</Button>}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {isAdmin && dashboard && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            ['Ongoing', dashboard.active, 'open'],
            ['Evaluation due', dashboard.evaluationDue, 'EvaluationDue'],
            ['Awaiting decision', dashboard.underReview, 'UnderReview'],
            ['Ending in 30 days', dashboard.endingIn30Days, 'open'],
            ['Overdue check-ins', dashboard.overdueReviews, 'open'],
            ['Conversion rate', dashboard.conversionRate != null ? `${dashboard.conversionRate}%` : '—', 'closed'],
          ].map(([label, value, filter]) => (
            <Grid item xs={6} sm={4} md={2} key={label as string}>
              <Paper variant="outlined" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setStatus(filter as string)}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 200 }}>
          {STATUS_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
        </TextField>
        <TextField size="small" placeholder="Search intern, mentor, role…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ flexGrow: 1 }} />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Intern</TableCell>
              <TableCell>Mentor</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Next check-in</TableCell>
              <TableCell>Latest progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>No internships found.</Typography></TableCell></TableRow>
            )}
            {filtered.map(r => {
              const decisionOverdue = !isClosed(r.status) && new Date(r.plannedEndDate).getTime() < today;
              return (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedId(r.id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.internName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.roleTitle}{r.department ? ` · ${r.department}` : ''}</Typography>
                  </TableCell>
                  <TableCell>{r.mentorName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {fmtDate(r.startDate)} – {fmtDate(r.actualEndDate ?? r.plannedEndDate)}
                    {!isClosed(r.status) && <Typography variant="caption" display="block" color="text.secondary">{r.daysRemaining} days left</Typography>}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Chip size="small" label={STATUS_META[r.status]?.label ?? r.status} color={STATUS_META[r.status]?.color ?? 'default'} />
                      {decisionOverdue && <Chip size="small" color="error" variant="outlined" label="Decision overdue" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {r.nextReviewDue ? fmtDate(r.nextReviewDue) : '—'}
                    {r.overdueReviews > 0 && <Chip size="small" color="error" label={`${r.overdueReviews} overdue`} sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    {r.latestProgressStatus
                      ? <Chip size="small" variant="outlined" label={PROGRESS_META[r.latestProgressStatus]?.label} color={PROGRESS_META[r.latestProgressStatus]?.color} />
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <InternshipDetailDialog internshipId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
      {createOpen && (
        <CreateInternshipDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => { setCreateOpen(false); load(); setSelectedId(id); }}
        />
      )}
    </Box>
  );
};

const CreateInternshipDialog: React.FC<{ onClose: () => void; onCreated: (id: number) => void }> = ({ onClose, onCreated }) => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [intern, setIntern] = useState<UserOption | null>(null);
  const [mentor, setMentor] = useState<UserOption | null>(null);
  const [roleTitle, setRoleTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [college, setCollege] = useState('');
  const [stipend, setStipend] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [durationMonths, setDurationMonths] = useState(6);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userService.getEmployees().then((data: any[]) => setUsers(data.map(u => ({ id: u.id, fullName: u.fullName })))).catch(() => setUsers([]));
  }, []);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await internshipService.create({
        userId: intern!.id,
        mentorId: mentor!.id,
        roleTitle, department, college, notes,
        stipend: stipend === '' ? null : Number(stipend),
        startDate,
        durationMonths,
      });
      onCreated(created.id);
    } catch (err) {
      setError(apiError(err, 'Failed to create internship.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New internship</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Autocomplete
            options={users}
            value={intern}
            getOptionLabel={o => o.fullName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setIntern(v)}
            renderInput={params => <TextField {...params} label="Intern *" helperText="The intern must already have an HRMS account (Employees screen)." />}
          />
          <Autocomplete
            options={users.filter(u => u.id !== intern?.id)}
            value={mentor}
            getOptionLabel={o => o.fullName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setMentor(v)}
            renderInput={params => <TextField {...params} label="Mentor *" helperText="Usually the intern's reporting manager." />}
          />
          <TextField label="Internship role / title *" placeholder="e.g. Software Development Intern" value={roleTitle} onChange={e => setRoleTitle(e.target.value)} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth type="date" label="Start date *" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} />
            <TextField fullWidth select label="Duration" value={durationMonths} onChange={e => setDurationMonths(Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6].map(m => <MenuItem key={m} value={m}>{m} month{m > 1 ? 's' : ''}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="Department" value={department} onChange={e => setDepartment(e.target.value)} />
            <TextField fullWidth type="number" label="Monthly stipend (₹)" value={stipend} onChange={e => setStipend(e.target.value)} />
          </Stack>
          <TextField label="College / University" value={college} onChange={e => setCollege(e.target.value)} />
          <TextField multiline minRows={2} label="Notes (HR only)" value={notes} onChange={e => setNotes(e.target.value)} />
          <Alert severity="info">
            The check-in schedule (monthly, mid-term and final evaluation) is created automatically, and the intern and mentor are emailed.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!intern || !mentor || !roleTitle.trim() || !startDate || saving} onClick={submit}>Create</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternshipManagement;
